"use client";

import React, { useEffect, useRef, useState } from "react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const galleryImages = [{
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1645251/img/estudio_producao_video.png",
  alt: "Equipe de produção audiovisual operando câmeras de cinema em estúdio"
}, {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1645251/img/planejamento_estrategico_digital.png",
  alt: "Mãos desenhando funil de vendas e estratégia de conteúdo em quadro branco"
}, {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1645251/img/pos_producao_detalhes.png",
  alt: "Interface de software de edição de vídeo com foco em correção de cor"
}];
const standardDetails = [{
  value: "inspection",
  title: "Produção de Alto Nível",
  content: "Não entregamos apenas vídeos, entregamos cinema. Nossa equipe utiliza equipamentos de última geração e técnicas narrativas avançadas para garantir que cada frame da sua marca transmita autoridade, profissionalismo e desejo imediato no seu público-alvo."
}, {
  value: "origins",
  title: "Estratégia de Conversão",
  content: "Beleza sem estratégia é apenas arte. Todo conteúdo produzido pelo nosso estúdio é fundamentado em dados e psicologia de consumo. Criamos narrativas que guiam o espectador através da jornada de compra, transformando visualizações em faturamento real."
}, {
  value: "support",
  title: "Ecossistema Digital",
  content: "Sua presença digital é tratada como um organismo vivo. Oferecemos suporte contínuo na implementação dos ativos, análise de performance e ajustes estratégicos para garantir que sua marca evolua constantemente e domine o seu nicho de mercado."
}];
function RevealImage({
  src,
  alt,
  index
}: {
  src: string;
  alt: string;
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <div data-section-id="230322" ref={ref} data-index={index} className={`transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-16"}`}>
      <img src={src} alt={alt} className="w-full h-auto object-cover rounded-lg shadow-xl aspect-[4/5] bg-muted/20" loading="lazy" />
    </div>;
}
export default function TheGalaxyStandard() {
  return <section id="diferencial-produção-estratégia" className="bg-background text-foreground relative py-24 md:py-32 overflow-hidden">
      {}
      <div className="absolute inset-0 pointer-events-none opacity-[0.05] mix-blend-overlay bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1645251/img/textura_estudio_suave.png)] bg-cover bg-center" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-24 items-start">
          {}
          <div className="lg:col-span-7 flex flex-col gap-12 md:gap-24">
            {galleryImages.map((image, i) => <RevealImage key={i} index={i} src={image.src} alt={image.alt} />)}
          </div>

          {}
          <div className="lg:col-span-5 lg:sticky lg:top-32 flex flex-col justify-center min-h-[50vh]">
            <div className="mb-10">
              <h2 className="font-default text-4xl md:text-5xl font-bold tracking-tight mb-6 text-foreground">
                O Diferencial que sua Marca Merece
              </h2>
              <p className="font-default text-lg text-muted-foreground leading-relaxed">
                Unimos a excelência técnica da produção audiovisual com a inteligência 
                do marketing estratégico. O resultado é uma presença digital magnética 
                que não apenas atrai olhares, mas converte clientes.
              </p>
            </div>

            <Accordion type="single" collapsible className="w-full border-t border-border" defaultValue="inspection">
              {standardDetails.map((detail, i) => <AccordionItem key={detail.value} value={detail.value} data-index={i} className="border-border">
                  <AccordionTrigger className="font-default text-xl font-semibold hover:text-primary transition-colors py-6">
                    {detail.title}
                  </AccordionTrigger>
                  <AccordionContent className="font-default text-base text-muted-foreground leading-relaxed pb-6 pr-6">
                    {detail.content}
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </div>
        </div>
      </div>
    </section>;
}